/**
 * Bulk Import Preview Component
 *
 * Displays parsed data from CSV/XLSX file with validation errors highlighted.
 */

import { CheckCircle, AlertTriangle, Loader2, FileSpreadsheet, Check, X } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { ParseResult, ParsedRow } from '../utils/bulkImportParser';
import { BULK_IMPORT_MAX_ROWS } from '../types/emissions';
import {
  FUEL_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
  SCOPE1_ACTIVITY_TYPES,
  SCOPE2_ACTIVITY_TYPES,
} from './form/types';

const ALL_ACTIVITY_TYPES = [...SCOPE1_ACTIVITY_TYPES, ...SCOPE2_ACTIVITY_TYPES];
const ACTIVITY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  ALL_ACTIVITY_TYPES.map(({ value, label }) => [value, label])
);

const ENUM_LABEL_MAPS: Record<string, Record<string, string>> = {
  'activity type': ACTIVITY_TYPE_LABELS,
  'fuel type': FUEL_TYPE_LABELS,
  'fuel type*': FUEL_TYPE_LABELS,
  'vehicle type': VEHICLE_TYPE_LABELS,
  'vehicle type*': VEHICLE_TYPE_LABELS,
  'gas / refrigerant type': GAS_TYPE_LABELS,
  'gas / refrigerant type*': GAS_TYPE_LABELS,
  'process type': PROCESS_TYPE_LABELS,
  'process type*': PROCESS_TYPE_LABELS,
};

interface BulkImportPreviewProps {
  parseResult: ParseResult;
  /**
   * Confirm/Cancel callbacks render the in-card button row when provided.
   * Omit both when this preview is rendered inside a wrapper that supplies
   * its own footer actions (e.g. `BulkImportModal`).
   */
  onConfirm?: () => void;
  onCancel?: () => void;
  isImporting?: boolean;
}

export const BulkImportPreview = ({
  parseResult,
  onConfirm,
  onCancel,
  isImporting = false,
}: BulkImportPreviewProps) => {
  const { rows, validCount, errorCount, headers } = parseResult;

  // Determine columns to display (limit to prevent overflow)
  const displayHeaders = headers.slice(0, 8); // First 8 columns
  const hasMoreColumns = headers.length > 7;

  return (
    <Card>
      <CardContent className='tw:p-4'>
        {/* Summary banner */}
        <div
          className={cn(
            'tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:p-3 tw:text-sm tw:mb-4',
            errorCount > 0
              ? 'tw:border-amber-200 tw:bg-amber-50 tw:text-amber-800'
              : 'tw:border-emerald-200 tw:bg-emerald-50 tw:text-emerald-800'
          )}
        >
          {errorCount > 0 ? (
            <AlertTriangle className='tw:size-4 tw:shrink-0' />
          ) : (
            <CheckCircle className='tw:size-4 tw:shrink-0' />
          )}
          <span>
            <strong>{validCount}</strong> valid row{validCount !== 1 ? 's' : ''}
            {errorCount > 0 && (
              <>
                , <strong>{errorCount}</strong> with errors
              </>
            )}
          </span>
        </div>

        {validCount > BULK_IMPORT_MAX_ROWS && (
          <div className='tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-sm tw:mb-4 tw:text-red-800'>
            <AlertTriangle className='tw:size-4 tw:shrink-0' />
            <span>
              Maximum <strong>{BULK_IMPORT_MAX_ROWS}</strong> rows per import.
              Please split your file into smaller batches.
            </span>
          </div>
        )}

        {/* Data table */}
        {rows.length > 0 ? (
          <div className='tw:overflow-x-auto tw:max-h-[700px] tw:overflow-y-auto tw:mb-4'>
            <table className='tw:w-full tw:text-sm'>
              <thead className='background-ThemeBgExtralight tw:sticky tw:top-0 tw:z-10 tw:shadow-[0_1px_0_0_theme(colors.border)]'>
                <tr>
                  <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium tw:w-14'>Row</th>
                  <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium tw:w-24'>Status</th>
                  {displayHeaders.map((h) => (
                    <th key={h} className='tw:text-left tw:px-3 tw:py-2 tw:font-medium'>{h}</th>
                  ))}
                  {hasMoreColumns && <th className='tw:px-3 tw:py-2'>...</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <PreviewRow key={row.rowNumber} row={row} headers={displayHeaders} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='tw:text-center tw:py-8 tw:text-muted-foreground tw:mb-4'>
            <FileSpreadsheet className='tw:size-10 tw:opacity-25 tw:mx-auto tw:mb-2' />
            <p className='tw:mb-0'>No data found in file</p>
          </div>
        )}

        {/* Actions — only rendered when the parent supplies callbacks. The
            modal wrapper (BulkImportModal) supplies its own footer instead. */}
        <div className={cn('tw:flex tw:items-center', (onConfirm || onCancel) ? 'tw:justify-between' : 'tw:justify-start')}>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
            {errorCount > 0
              ? 'Rows with errors will be skipped during import.'
              : 'All rows will be imported.'}
          </p>
          {(onConfirm || onCancel) && (
            <div className='tw:flex tw:gap-2'>
              {onCancel && (
                <Button variant='outline' onClick={onCancel} disabled={isImporting}>
                  Cancel
                </Button>
              )}
              {onConfirm && (
                <Button onClick={onConfirm} disabled={validCount === 0 || validCount > BULK_IMPORT_MAX_ROWS || isImporting}>
                  {isImporting ? (
                    <>
                      <Loader2 className='tw:size-4 tw:animate-spin' />
                      Importing...
                    </>
                  ) : (
                    <>Import {validCount} Activit{validCount === 1 ? 'y' : 'ies'}</>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Individual row component
 */
interface PreviewRowProps {
  row: ParsedRow;
  headers: string[];
}

/**
 * Get display value for a cell - uses parsed data for date columns
 * to show converted dates instead of Excel serial numbers
 */
const getDisplayValue = (row: ParsedRow, header: string): string => {
  const normalizedHeader = header.toLowerCase().replace(/\*/g, '').trim();

  if (normalizedHeader.includes('period start') || normalizedHeader === 'periodstart' || normalizedHeader === 'start date') {
    return row.data.periodStart || String(row.rawData[header] ?? '');
  }
  if (normalizedHeader.includes('period end') || normalizedHeader === 'periodend' || normalizedHeader === 'end date') {
    return row.data.periodEnd || String(row.rawData[header] ?? '');
  }

  const rawValue = String(row.rawData[header] ?? '');
  const labelMap = ENUM_LABEL_MAPS[normalizedHeader];
  if (labelMap && rawValue) {
    return labelMap[rawValue] ?? rawValue;
  }

  return rawValue;
};

const PreviewRow = ({ row, headers }: PreviewRowProps) => {
  const { rowNumber, errors, isValid } = row;
  const errorMessage = errors.map((e) => e.message).join(', ');

  return (
    <tr className={cn('tw:border-b', !isValid && 'tw:bg-red-50')}>
      <td className='tw:px-3 tw:py-2 tw:text-muted-foreground'>{rowNumber}</td>
      <td className='tw:px-3 tw:py-2'>
        {isValid ? (
          <span className='tw:flex tw:items-center tw:gap-1 tw:text-emerald-700'>
            <Check className='tw:size-3' />
            Valid
          </span>
        ) : (
          <span className='tw:flex tw:items-center tw:gap-1 tw:text-destructive' title={errorMessage}>
            <X className='tw:size-3' />
            {errors.length} error{errors.length !== 1 ? 's' : ''}
          </span>
        )}
      </td>
      {headers.map((header) => (
        <td
          key={header}
          className={cn(
            'tw:px-3 tw:py-2',
            errors.some((e) => header.toLowerCase().includes(e.field.toLowerCase())) && 'tw:bg-red-100'
          )}
        >
          {getDisplayValue(row, header)}
        </td>
      ))}
      {headers.length < Object.keys(row.rawData).length && (
        <td className='tw:px-3 tw:py-2 tw:text-muted-foreground'>...</td>
      )}
    </tr>
  );
};

/**
 * Error summary component for displaying all errors
 */
interface ErrorSummaryProps {
  parseResult: ParseResult;
}

export const ErrorSummary = ({ parseResult }: ErrorSummaryProps) => {
  const errorRows = parseResult.rows.filter((r) => !r.isValid);

  if (errorRows.length === 0) {
    return null;
  }

  return (
    <div className='tw:rounded-lg tw:border tw:border-destructive/30 tw:bg-destructive/5 tw:p-4 tw:mt-3'>
      <h5 className='tw:font-semibold tw:text-sm tw:flex tw:items-center tw:gap-2 tw:mb-3 tw:text-destructive'>
        <AlertTriangle className='tw:size-4' />
        Validation Errors
      </h5>
      <ul className='tw:text-sm tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0 tw:text-destructive/80'>
        {errorRows.slice(0, 10).map((row) => (
          <li key={row.rowNumber}>
            <strong>Row {row.rowNumber}:</strong> {row.errors.map((e) => e.message).join(', ')}
          </li>
        ))}
        {errorRows.length > 10 && (
          <li className='tw:text-muted-foreground'>...and {errorRows.length - 10} more errors</li>
        )}
      </ul>
    </div>
  );
};
